import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Alert as MuiAlert,
  Backdrop,
  Box,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  Divider,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import SelectWrapper from "./SmallSelectBox";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid, GridToolbar, GridFooter } from "@mui/x-data-grid";
import moment from "moment";
import ViewCustomerInvoiceDialog from "../components/ViewCustomerInvoiceDialog";
import { getHoverTextColor } from "../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import ViewHistoricClaimDialog from "./ViewHistoricClaimDialog";
import apiUtils from "../utils/apiUtils";
import SelectWrapperDB from "../components/SelectBoxDataGridCell";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BankAccountSelectBox from "./BankAccountSelectBox";
import ClaimXmlValidationDialog from "./ClaimXmlValidationDialog";
import ClaimLockTncConfirmDialog from "./ClaimLockTncConfirmDialog";
import {
  CLAIM_XML_DOWNLOAD_UNAVAILABLE_MESSAGE,
  CLAIM_XML_VALIDATION_ORIGIN_UNAVAILABLE_MESSAGE,
  CLAIM_XML_VALIDATION_UNAVAILABLE_MESSAGE,
  getClaimXmlDownloadUrl,
  hasClaimXmlDownloadForEntity,
  isEuVatRefundCountry,
  isNlEntityCountry,
} from "../utils/euVatRefundCountries";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITIES } from "../auth/permissions";
import {
  POLAND_COUNTRY_ID,
  getMissingPolishXmlNawFields,
  isGermanSteuerNummerMissing,
} from "../utils/taxOfficeUtils";

const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

/**
 * Backend enum translation is disabled.
 * Always send numeric enum values for claim invoice download.
 *
 * Mode:
 * 1 = AllSeparate
 * 2 = MergedSinglePdf
 * 3 = SortedByInvoiceDate
 * 4 = SortedByVatAmount
 *
 * SortDirection:
 * 1 = Ascending
 * 2 = Descending
 *
 * Important:
 * sortDirection only applies to mode 3 and 4.
 * The old "*Descending" enum names are duplicates of mode 3 and 4.
 */
const CLAIM_INVOICE_DOWNLOAD_MODE = Object.freeze({
  AllSeparate: 1,
  MergedSinglePdf: 2,
  SortedByInvoiceDate: 3,
  SortedByVatAmount: 4,
});

const CLAIM_INVOICE_DOWNLOAD_SORT_DIRECTION = Object.freeze({
  Ascending: 1,
  Descending: 2,
});

const PLACEHOLDER_DATE = "0001-01-01";

const DATE_WARNING_HELPER_PROPS = {
  role: "status",
  sx: {
    color: "warning.main",
    marginLeft: 0,
    mt: 0.5,
  },
};

const hasUsableDate = (date) => {
  if (!date || date === PLACEHOLDER_DATE) return false;
  const parsed = moment(date, "YYYY-MM-DD", true);
  return parsed.isValid() && parsed.year() > 1900;
};

const getDateFieldWarnings = ({ claimDate, notificationDate, reimbursementDate, payDate }) => {
  const today = moment().startOf("day");
  const oneYearAgo = moment().subtract(1, "year").startOf("day");
  const warnings = {
    claimDate: [],
    notificationDate: [],
    reimbursementDate: [],
    payDate: [],
  };

  if (hasUsableDate(claimDate)) {
    const parsedClaimDate = moment(claimDate, "YYYY-MM-DD", true);
    if (!parsedClaimDate.isAfter(oneYearAgo)) {
      warnings.claimDate.push("This date is one year old or older.");
    }
    if (parsedClaimDate.isAfter(today)) {
      warnings.claimDate.push("This date is in the future.");
    }
  }

  if (hasUsableDate(notificationDate)) {
    const parsedNotificationDate = moment(notificationDate, "YYYY-MM-DD", true);
    if (hasUsableDate(claimDate) && parsedNotificationDate.isBefore(moment(claimDate, "YYYY-MM-DD", true), "day")) {
      warnings.notificationDate.push("This date is earlier than the claim date.");
    }
    if (parsedNotificationDate.isAfter(today)) {
      warnings.notificationDate.push("This date is in the future.");
    }
  }

  if (hasUsableDate(reimbursementDate)) {
    const parsedReimbursementDate = moment(reimbursementDate, "YYYY-MM-DD", true);
    if (
      hasUsableDate(notificationDate) &&
      parsedReimbursementDate.isBefore(moment(notificationDate, "YYYY-MM-DD", true), "day")
    ) {
      warnings.reimbursementDate.push("This date is earlier than the notification date.");
    }
    if (parsedReimbursementDate.isAfter(today)) {
      warnings.reimbursementDate.push("This date is in the future.");
    }
  }

  if (hasUsableDate(payDate)) {
    const parsedPayDate = moment(payDate, "YYYY-MM-DD", true);
    if (hasUsableDate(claimDate) && parsedPayDate.isBefore(moment(claimDate, "YYYY-MM-DD", true), "day")) {
      warnings.payDate.push("This date is earlier than the claim date.");
    }
    if (parsedPayDate.isAfter(today)) {
      warnings.payDate.push("This date is in the future.");
    }
  }

  return warnings;
};

export default function ViewClaimDialog(props) {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const canWriteClaims = hasPermission(CAPABILITIES.claimsWrite, "W");
  const canSubmitClaimXml = hasPermission(CAPABILITIES.claimsSubmitXml, "X");
  const canUndoClaim = hasPermission(CAPABILITIES.claimsUndo, "X");
  const supplierData = apiUtils.useApiData("/api/v1/suppliers");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const [productData, setProductData] = useState([]);
  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/productTypes").then((response) => setProductData(response.data));
    }
    fetchData();
  }, []);
  const invoiceLabelData = apiUtils.useApiData("/api/v1/invoiceLabels");
  const invoiceStateData = apiUtils.useApiData("/api/v1/invoiceStates");
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const claimStateData = apiUtils.useApiData("/api/v1/claimStates");
  const bankAccounts = apiUtils.useApiData("/api/v1/bankAccounts");
  const documentTypes = apiUtils.useApiData("/api/v1/documenttypes");
  const cesDocumentTypeId = useMemo(() => {
    if (!Array.isArray(documentTypes) || documentTypes.length === 0) return null;
    const ces = documentTypes.find((d) => String(d.shortCode ?? "").toUpperCase() === "CES");
    return ces?.documentTypeId ?? null;
  }, [documentTypes]);
  console.log("bankAccounts", bankAccounts);

  const { viewClaimDialog, setViewClaimDialog, refreshTable } = props;
  var customerId = viewClaimDialog?.custInfo?.[0]?.customerId;
  var customerNumber = viewClaimDialog?.custInfo?.[0]?.customerNumber;
  const [hasPreFinancedInvoices, setHasPreFinancedInvoices] = useState(false);
  const [payableToDelta, setPayableToDelta] = useState(false);
  const deltaPayableIbanPreFinanced = "NL32 INGB 0116 9214 98";
  const deltaPayableIbanStandard = "NL26 INGB 0104 2133 45";
  const [invoiceGridData, setInvoiceGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingClaim, setUpdatingClaim] = useState(false);
  const [isUndoingClaimed, setIsUndoingClaimed] = useState(false);
  const [undoClaimedError, setUndoClaimedError] = useState("");
  const [openUndoClaimedConfirmation, setOpenUndoClaimedConfirmation] = useState(false);
  const [openDeleteClaimConfirmation, setOpenDeleteClaimConfirmation] = useState(false);
  const [pageSize, setPageSize] = React.useState(15);
  const [dataVersion, setDataVersion] = useState(0);

  const [selectedPaymentTypeId, setSelectedPaymentTypeId] = useState("");
  const [selectedClaimStateId, setSelectedClaimStateId] = useState("");
  const [selectedLiters, setSelectedLiters] = useState("");
  const [selectedAdvancedRefundAmountEUR, setSelectedAdvancedRefundAmountEUR] = useState("");
  const [selectedClaimDate, setSelectedClaimDate] = useState("");
  const [selectedNotificationDate, setSelectedNotificationDate] = useState("");
  const [selectedReimbursementDate, setSelectedReimbursementDate] = useState("");
  const [selectedPaymentDate, setSelectedPaymentDate] = useState("");
  const [selectedBankAccount, setSelectedBankAccount] = useState(() => {
    return viewClaimDialog?.params?.payableBankAccountId;
  });
  const [selectedClaimPreview, setSelectedClaimPreview] = useState([]);
  const [selectedTaxOfficeRefNo, setSelectedTaxOfficeRefNo] = useState("");
  const [taxOfficeRefNoError, setTaxOfficeRefNoError] = useState("");
  const [openCorrectionConfirmation, setOpenCorrectionConfirmation] = useState(false);

  // MSR Documents selection state
  const [selectedMSRDocuments, setSelectedMSRDocuments] = useState([]);
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const [vatRequiredDialog, setVatRequiredDialog] = useState({ isOpen: false });
  const [uncheckedDocumentsDialog, setUncheckedDocumentsDialog] = useState({
    isOpen: false,
    documents: [],
  });
  const [cesMissingDialog, setCesMissingDialog] = useState({ isOpen: false });
  const [taxOfficeNumberDialog, setTaxOfficeNumberDialog] = useState({ isOpen: false });
  const [steuerNummerDialog, setSteuerNummerDialog] = useState({ isOpen: false });
  const [missingPolishNawDialog, setMissingPolishNawDialog] = useState({
    isOpen: false,
    fields: [],
  });
  const [lockPaymentRequiredDialog, setLockPaymentRequiredDialog] = useState({ isOpen: false });
  const [claimXmlValidation, setClaimXmlValidation] = useState(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationOverride, setValidationOverride] = useState(false);
  const [validatingClaimXml, setValidatingClaimXml] = useState(false);
  const canDownloadOrSubmitClaimXml = useMemo(() => {
    if (!claimXmlValidation) {
      return false;
    }

    if (claimXmlValidation.hasErrors) {
      return false;
    }

    if (claimXmlValidation.hasWarnings) {
      return validationOverride;
    }

    return claimXmlValidation.isValid;
  }, [claimXmlValidation, validationOverride]);
  // Fetch customer documents to check which documents are available
  useEffect(() => {
    if (customerId) {
      const fetchCustomerDocuments = async () => {
        try {
          const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
          setCustomerDocuments(response.data || []);
        } catch (error) {
          setCustomerDocuments([]);
        }
      };
      fetchCustomerDocuments();
    }
  }, [customerId]);

  // Initialize MSR documents selection with saved msrDocuments from claim
  useEffect(() => {
    console.log("viewClaimDialog", viewClaimDialog);
    if (viewClaimDialog.params && viewClaimDialog.params.msrDocuments) {
      // Convert comma-separated string back to array of integers
      const msrDocsArray =
        typeof viewClaimDialog.params.msrDocuments === "string"
          ? viewClaimDialog.params.msrDocuments
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id))
          : viewClaimDialog.params.msrDocuments; // Fallback for array format
      setSelectedMSRDocuments(msrDocsArray);
      console.log("msrDocsArray", msrDocsArray);
    } else {
      // Start with empty selection if no saved data
      setSelectedMSRDocuments([]);
    }
  }, [viewClaimDialog.params]);

  useEffect(() => {
    if (!viewClaimDialog.isOpen) {
      setClaimXmlValidation(null);
      setValidationOverride(false);
      setValidationDialogOpen(false);
    }
  }, [viewClaimDialog.isOpen]);

  // Automatically check available documents when customer documents and document types are loaded
  // Only if no existing MSR documents are saved in the claim
  useEffect(() => {
    if (customerId && customerDocuments.length > 0 && documentTypes.length > 0 && viewClaimDialog.params?.countryId) {
      // Check if there are already saved MSR documents in the claim
      const hasExistingMsrDocuments = viewClaimDialog.params?.msrDocuments && viewClaimDialog.params.msrDocuments.trim() !== "";

      // Only auto-select if no existing MSR documents are saved
      if (!hasExistingMsrDocuments) {
        const claimCountryId = viewClaimDialog.params.countryId;

        // Determine which document types should be automatically checked
        const autoSelectedDocs = [];

        documentTypes
          .filter((doc) => !doc.edRequired) // Only include non-ED required documents (same as MSR list)
          .filter((doc) => doc.vatRequired) // Only include non-ED required documents (same as MSR list)
          .forEach((docType) => {
            // Check if the document exists for this customer and country
            const documentExists = customerDocuments.some(
              (customerDoc) =>
                Number(customerDoc.documentType) === Number(docType.documentTypeId) &&
                Number(customerDoc.countryId) === Number(claimCountryId) &&
                Number(customerDoc.deleted) === 0
            );

            if (documentExists) {
              autoSelectedDocs.push(docType.documentTypeId);
            }
          });

        // Update selectedMSRDocuments with automatically selected documents
        setSelectedMSRDocuments(autoSelectedDocs);
        console.log("autoSelectedDocs", autoSelectedDocs);
      }
    }
  }, [customerId, customerDocuments, documentTypes, viewClaimDialog.params?.countryId, viewClaimDialog.params?.msrDocuments]);

  // Prefinanciering: Cessie mededeling (CES) in selectie opnemen wanneer het document beschikbaar is
  useEffect(() => {
    if (!hasPreFinancedInvoices || cesDocumentTypeId == null) return;

    const claimCountryId = viewClaimDialog.params?.countryId;
    if (!claimCountryId) return;

    const cesAvailable = customerDocuments.some(
      (doc) =>
        Number(doc.documentType) === Number(cesDocumentTypeId) &&
        Number(doc.countryId) === Number(claimCountryId) &&
        Number(doc.deleted) === 0
    );
    if (!cesAvailable) return;

    setSelectedMSRDocuments((prev) => {
      if (prev.includes(cesDocumentTypeId)) return prev;
      return [...prev, cesDocumentTypeId];
    });
  }, [hasPreFinancedInvoices, cesDocumentTypeId, customerDocuments, viewClaimDialog.params?.countryId]);

  const isPreFinanceCesDocumentMissing = useMemo(() => {
    if (!hasPreFinancedInvoices || cesDocumentTypeId == null) return false;
    const claimCountryId = viewClaimDialog.params?.countryId;
    if (!claimCountryId) return true;
    return !customerDocuments.some(
      (doc) =>
        Number(doc.documentType) === Number(cesDocumentTypeId) &&
        Number(doc.countryId) === Number(claimCountryId) &&
        Number(doc.deleted) === 0
    );
  }, [hasPreFinancedInvoices, cesDocumentTypeId, customerDocuments, viewClaimDialog.params?.countryId]);

  // Historic claims state
  const [historicClaims, setHistoricClaims] = useState([]);
  const [selectedHistoricClaimId, setSelectedHistoricClaimId] = useState("");

  // Historic Claim dialog (read-only variant)
  const [viewHistoricClaimDialog, setViewHistoricClaimDialog] = useState({
    isOpen: false,
    title: "",
    params: {},
  });

  const [invoiceCount, setInvoiceCount] = useState(0);
  const [totalGrossAmount, setTotalGrossAmount] = useState(0);
  const [totalVATAmount, setTotalVATAmount] = useState(0);
  const [gridTotalGrossAmount, setGridTotalGrossAmount] = useState(0);
  const [gridTotalVATAmount, setGridTotalVATAmount] = useState(0);
  const [bankAccountsFiltered, setBankAccountsFiltered] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const tncConfirmResolverRef = useRef(null);
  const lockPreviewRef = useRef(null);
  const initialClaimStateIdRef = useRef(null);
  const [tncConfirmDialog, setTncConfirmDialog] = useState({ isOpen: false, preview: null });
  const [open, setOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  };
  const popupStyle = {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "5px",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
    textAlign: "center",
  };

  // Laad historic claims op basis van de huidige claimcode prefix
  useEffect(() => {
    const claimId = viewClaimDialog?.params?.claimId;
    const claimCodeParam = viewClaimDialog?.params?.claimCode;
    if (!viewClaimDialog?.isOpen || !claimId || !claimCodeParam) {
      setHistoricClaims([]);
      return;
    }

    axios
      .get("/api/v1/claims/historicClaimsByFullClaimCode/" + claimCodeParam)
      .then((response) => {
        if (selectedClaimId.current !== claimId) {
          return;
        }
        setHistoricClaims(response.data);
      })
      .catch((error) => console.error("Error fetching historic claims:", error));
  }, [viewClaimDialog?.isOpen, viewClaimDialog?.params?.claimId, viewClaimDialog?.params?.claimCode]);

  const [alertInfo, setAlertInfo] = useState({ message: "", type: "" });
  const gridRef = React.useRef();

  const [claimInvoiceDownloadMenuAnchorEl, setClaimInvoiceDownloadMenuAnchorEl] = useState(null);
  const [claimInvoiceDownloadSubMenuAnchorEl, setClaimInvoiceDownloadSubMenuAnchorEl] = useState(null);
  const [claimInvoiceDownloadSubMenuMode, setClaimInvoiceDownloadSubMenuMode] = useState(null);
  const [isClaimInvoiceDownloadInProgress, setIsClaimInvoiceDownloadInProgress] = useState(false);
  const claimInvoiceDownloadButtonRef = useRef(null);
  const claimInvoiceDownloadMenuPaperRef = useRef(null);
  const claimInvoiceDownloadSubMenuPaperRef = useRef(null);

  const isRelatedTargetInside = useCallback((event, elementRefs) => {
    const relatedTarget = event?.relatedTarget;
    if (!relatedTarget) return false;

    return elementRefs.some((ref) => {
      const el = ref?.current;
      return el ? el.contains(relatedTarget) : false;
    });
  }, []);

  const calculateTotals = useCallback(() => {
    if (invoiceGridData.length === 0) {
      return;
    }
    let invoiceCount = 0;
    let totalGrossAmount = 0;
    let totalVATAmount = 0;
    let gridTotalGrossAmount = 0;
    let gridTotalVATAmount = 0;
    invoiceGridData.forEach((row) => {
      gridTotalGrossAmount += row.invoiceGrossAmount;
      gridTotalVATAmount += row.invoiceVATAmountLocalCurrency;
      if (row.invoiceStateId === 3) {
        invoiceCount++;
        totalGrossAmount += row.invoiceGrossAmount;
        totalVATAmount += row.invoiceVATAmountLocalCurrency;
      }
    });
    setInvoiceCount(invoiceCount);
    setTotalGrossAmount(totalGrossAmount);
    setTotalVATAmount(totalVATAmount);
    setGridTotalGrossAmount(gridTotalGrossAmount);
    setGridTotalVATAmount(gridTotalVATAmount);
  }, [invoiceGridData]);

  const selectedClaimId = useRef();
  const [loadedClaimId, setLoadedClaimId] = useState(null);

  const resetClaimViewState = () => {
    setLoadedClaimId(null);
    setSelectedClaimPreview([]);
    setInvoiceGridData([]);
    setSelectedTaxOfficeRefNo("");
    setTaxOfficeRefNoError("");
    setUndoClaimedError("");
    setIsUndoingClaimed(false);
    setOpenUndoClaimedConfirmation(false);
    setOpenDeleteClaimConfirmation(false);
    setSelectedPaymentTypeId("");
    setSelectedClaimStateId("");
    setSelectedLiters("");
    setSelectedAdvancedRefundAmountEUR("");
    setSelectedClaimDate("");
    setSelectedNotificationDate("");
    setSelectedReimbursementDate("");
    setSelectedPaymentDate("");
    setSelectedBankAccount("");
    setSelectedMSRDocuments([]);
    setClaimXmlValidation(null);
    setValidationOverride(false);
    setHasPreFinancedInvoices(false);
    setPayableToDelta(false);
    setHistoricClaims([]);
    setSelectedHistoricClaimId("");
    initialClaimStateIdRef.current = null;
    setLoading(true);
  };

  const handleCloseClaimDialog = () => {
    // Only flip isOpen; keep params/custInfo intact so exit-transition render cannot crash.
    setViewClaimDialog((prev) => ({ ...prev, isOpen: false }));
    selectedClaimId.current = undefined;
    resetClaimViewState();
  };

  // useLayoutEffect: clear + fetch before browser paint so old claim data never flashes.
  useLayoutEffect(() => {
    if (!viewClaimDialog?.isOpen) {
      selectedClaimId.current = undefined;
      setLoadedClaimId(null);
      return;
    }

    const claimId = viewClaimDialog?.params?.claimId;
    if (!claimId) {
      return;
    }

    if (selectedClaimId.current !== claimId) {
      resetClaimViewState();
    } else {
      setLoading(true);
    }
    selectedClaimId.current = claimId;
    getPreviewData();
  }, [viewClaimDialog?.isOpen, viewClaimDialog?.params?.claimId, dataVersion]);

  const handleOpenClaimInvoiceDownloadMenu = useCallback(
    (event) => {
      setClaimInvoiceDownloadMenuAnchorEl(event.currentTarget);
      setClaimInvoiceDownloadSubMenuAnchorEl(null);
      setClaimInvoiceDownloadSubMenuMode(null);
    },
    []
  );

  const handleCloseClaimInvoiceDownloadMenu = useCallback(() => {
    setClaimInvoiceDownloadMenuAnchorEl(null);
    setClaimInvoiceDownloadSubMenuAnchorEl(null);
    setClaimInvoiceDownloadSubMenuMode(null);
  }, []);

  const handleOpenClaimInvoiceDownloadSubMenu = useCallback(
    (event, mode) => {
      setClaimInvoiceDownloadSubMenuAnchorEl(event.currentTarget);
      setClaimInvoiceDownloadSubMenuMode(mode);
    },
    []
  );

  const handleCloseClaimInvoiceDownloadSubMenu = useCallback(() => {
    setClaimInvoiceDownloadSubMenuAnchorEl(null);
    setClaimInvoiceDownloadSubMenuMode(null);
  }, []);

  const handleClaimInvoiceDownloadMenuMouseLeave = useCallback(
    (event) => {
      if (
        isRelatedTargetInside(event, [
          claimInvoiceDownloadButtonRef,
          claimInvoiceDownloadMenuPaperRef,
          claimInvoiceDownloadSubMenuPaperRef,
        ])
      ) {
        return;
      }

      handleCloseClaimInvoiceDownloadMenu();
    },
    [handleCloseClaimInvoiceDownloadMenu, isRelatedTargetInside]
  );

  const handleClaimInvoiceDownloadSubMenuMouseLeave = useCallback(
    (event) => {
      if (
        isRelatedTargetInside(event, [
          claimInvoiceDownloadButtonRef,
          claimInvoiceDownloadMenuPaperRef,
          claimInvoiceDownloadSubMenuPaperRef,
        ])
      ) {
        return;
      }

      handleCloseClaimInvoiceDownloadMenu();
    },
    [handleCloseClaimInvoiceDownloadMenu, isRelatedTargetInside]
  );

  const getClaimInvoiceDownloadErrorMessage = useCallback(async (error) => {
    const errorData = error?.response?.data;

    if (errorData instanceof Blob) {
      try {
        const errorText = await errorData.text();
        const parsedError = JSON.parse(errorText);
        return parsedError?.detail || parsedError?.message || errorText || "Error downloading claim invoices";
      } catch (parseError) {
        try {
          const fallbackText = await errorData.text();
          return fallbackText || "Error downloading claim invoices";
        } catch (blobReadError) {
          return "Error downloading claim invoices";
        }
      }
    }

    return error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Error downloading claim invoices";
  }, []);

  const startClaimInvoiceDownload = useCallback(
    async ({ mode, sortDirection }) => {
      const claimId = selectedClaimPreview?.claimId || selectedClaimId.current;
      if (!claimId) {
        setAlertInfo({ message: "No claim selected", type: "error" });
        setTimeout(() => setAlertInfo({ message: "", type: "" }), 5000);
        return;
      }

      if (!invoiceGridData || invoiceGridData.length <= 0) {
        setAlertInfo({ message: "This claim contains no invoices", type: "error" });
        setTimeout(() => setAlertInfo({ message: "", type: "" }), 5000);
        return;
      }

      setIsClaimInvoiceDownloadInProgress(true);
      handleCloseClaimInvoiceDownloadMenu();

      try {
        const requestBody = { mode };
        if (
          mode === CLAIM_INVOICE_DOWNLOAD_MODE.SortedByInvoiceDate ||
          mode === CLAIM_INVOICE_DOWNLOAD_MODE.SortedByVatAmount
        ) {
          requestBody.sortDirection = sortDirection || CLAIM_INVOICE_DOWNLOAD_SORT_DIRECTION.Ascending;
        }

        const response = await axios.post(`/api/v1/customerInvoices/customClaimInvoiceDownload/${claimId}`, requestBody, {
          responseType: "blob",
        });

        const contentDisposition = response?.headers?.["content-disposition"];
        let filename = `claim-${claimId}-invoices.zip`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches?.[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        const downloadUrl = URL.createObjectURL(response.data);
        const downloadLink = document.createElement("a");
        downloadLink.href = downloadUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);

        setAlertInfo({ message: "Claim invoice ZIP download started", type: "success" });
      } catch (error) {
        const errorMessage = await getClaimInvoiceDownloadErrorMessage(error);
        setAlertInfo({ message: errorMessage, type: "error" });
      } finally {
        setTimeout(() => setAlertInfo({ message: "", type: "" }), 5000);
        setIsClaimInvoiceDownloadInProgress(false);
      }
    },
    [
      getClaimInvoiceDownloadErrorMessage,
      handleCloseClaimInvoiceDownloadMenu,
      invoiceGridData,
      selectedClaimId,
      selectedClaimPreview?.claimId,
    ]
  );

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
    productInfo: [],
  });

  const [claimCode, setClaimCode] = useState("");
  const claimCodeSuffix = claimCode.slice(-2);
  const requestedClaimId = viewClaimDialog?.isOpen ? viewClaimDialog?.params?.claimId : null;
  // Lookups are re-fetched on remount; don't open the grid until they are available.
  const areLookupsReady =
    Array.isArray(countryData) &&
    countryData.length > 0 &&
    Array.isArray(currencyData) &&
    currencyData.length > 0 &&
    Array.isArray(invoiceStateData) &&
    invoiceStateData.length > 0 &&
    Array.isArray(claimStateData) &&
    claimStateData.length > 0;
  // Only treat the dialog as ready after the fetch for THIS claimId completed.
  const isClaimDataReady =
    Boolean(requestedClaimId) &&
    areLookupsReady &&
    loadedClaimId != null &&
    Number(loadedClaimId) === Number(requestedClaimId) &&
    Number(selectedClaimPreview?.claimId) === Number(requestedClaimId);
  const showClaimLoading = Boolean(viewClaimDialog?.isOpen && !isClaimDataReady);
  useEffect(() => {
    setClaimCode(selectedClaimPreview.claimCode || "");
  }, [selectedClaimPreview.claimCode]);

  const isUpdateDisabled = useMemo(() => {
    if (!canWriteClaims) return true;
    if (updatingClaim) return true;
    if (taxOfficeRefNoError) return true;

    const isReimbursedState = parseInt(selectedClaimStateId) === 16;
    const endsWith01 = claimCode.endsWith("01");

    if (isReimbursedState && endsWith01) {
      const invalidDate = (date) => {
        if (!date || date === "0001-01-01" || !moment(date, "YYYY-MM-DD", true).isValid()) return true;
        const minDate = moment().subtract(2, "years").startOf("day");
        return moment(date).isBefore(minDate);
      };

      return (
        invalidDate(selectedClaimDate) ||
        invalidDate(selectedNotificationDate) ||
        invalidDate(selectedReimbursementDate) ||
        invalidDate(selectedPaymentDate)
      );
    }

    return false;
  }, [
    updatingClaim,
    taxOfficeRefNoError,
    selectedClaimStateId,
    selectedClaimDate,
    selectedNotificationDate,
    selectedReimbursementDate,
    selectedPaymentDate,
    claimCode,
    canWriteClaims,
  ]);

  const dateFieldWarnings = useMemo(
    () =>
      getDateFieldWarnings({
        claimDate: selectedClaimDate,
        notificationDate: selectedNotificationDate,
        reimbursementDate: selectedReimbursementDate,
        payDate: selectedPaymentDate,
      }),
    [selectedClaimDate, selectedNotificationDate, selectedReimbursementDate, selectedPaymentDate]
  );

  var country = selectedClaimPreview.country ? selectedClaimPreview.country : 0;
  var year = selectedClaimPreview.year ? selectedClaimPreview.year : 0;
  var startMonth = selectedClaimPreview.startMonth ? selectedClaimPreview.startMonth : 0;
  var endMonth = selectedClaimPreview.endMonth ? selectedClaimPreview.endMonth : 0;
  var claimStateId = selectedClaimPreview.claimStateId ? selectedClaimPreview.claimStateId : 0;
  var initPayableBankAccountId = selectedClaimPreview.PayableBankAccountId ? selectedClaimPreview.PayableBankAccountId : 0;

  useEffect(() => {
    if (!selectedClaimPreview?.claimId) {
      return;
    }

    setSelectedPaymentTypeId(selectedClaimPreview.paymentTypeId);
    setSelectedClaimStateId(selectedClaimPreview.claimStateId);
    if (selectedClaimPreview?.claimStateId != null && selectedClaimPreview?.claimStateId !== "") {
      initialClaimStateIdRef.current = parseInt(selectedClaimPreview.claimStateId, 10);
    }
    setSelectedBankAccount(selectedClaimPreview.payableBankAccountId);
    setSelectedLiters(selectedClaimPreview.liters);
    setSelectedAdvancedRefundAmountEUR(selectedClaimPreview.advancedRefundAmountEUR);
    setSelectedClaimDate(moment(selectedClaimPreview.claimDate).format("YYYY-MM-DD"));
    setSelectedNotificationDate(moment(selectedClaimPreview.notificationDate).format("YYYY-MM-DD"));
    setSelectedReimbursementDate(moment(selectedClaimPreview.reimbursementDate).format("YYYY-MM-DD"));
    setSelectedPaymentDate(moment(selectedClaimPreview.payDate).format("YYYY-MM-DD"));
    setBankAccountsFiltered(bankAccounts.filter((bankAccount) => bankAccount.entityId === parseInt(customerId)));
    setSelectedTaxOfficeRefNo(selectedClaimPreview.taxOfficeRefNo || "");
    setTaxOfficeRefNoError("");
  }, [selectedClaimPreview, bankAccounts, customerId]);

  useEffect(() => {
    if (historicClaims.length > 0) {
      setSelectedHistoricClaimId("default");
    } else {
      setSelectedHistoricClaimId("none");
    }
  }, [historicClaims]);

  // Bouw de dropdown opties voor historic claims:
  const historicOptions =
    historicClaims.length === 0
      ? [{ key: "none", value: "No Historic Claims Available" }]
      : [
          { key: "default", value: "Select Historic Claim" },
          ...historicClaims.map((claim) => ({
            key: claim.historicClaimId,
            value: claim.claimCode,
          })),
        ];
  const dropdownValue = selectedHistoricClaimId;

  const handleInvoiceLabelUpdate = (params) => {
    const rowIndex = invoiceGridData.findIndex((row) => row.customerInvoiceId === params.customerInvoiceId);
    invoiceGridData[rowIndex].invoiceLabelId = params.invoiceLabelId;
  };

  const handleInvoiceStateUpdate = (params) => {
    const rowIndex = invoiceGridData.findIndex((row) => row.customerInvoiceId === params.customerInvoiceId);
    invoiceGridData[rowIndex].invoiceStateId = params.invoiceStateId;
    let invoiceStateId = invoiceStateData.find((x) => x.invoiceStateShortCode === "TNC").invoiceStateId;
    if (params.invoiceStateId === invoiceStateId) {
      // extra logica indien nodig
    }
    calculateTotals();
  };

  const columns = [
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 0.5,
      hide: false,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      flex: 0.4,
      valueFormatter: (params) => moment(params?.value).format("DD-MM-YYYY"),
    },
    {
      field: "supplierId",
      headerName: "Supplier",
      flex: 0.5,
      valueGetter: (params) => supplierData.find((supplier) => supplier.supplierId === params.row.supplierId)?.supplierName,
    },
    {
      field: "productTypeId",
      headerName: "Product",
      flex: 0.4,
      valueGetter: (params) =>
        params.row.claimProductTypeId === -1
          ? "10 Custom"
          : productData.find((product) => product.productTypeId === params.row.claimProductTypeId)?.productShortDescription,
    },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.3,
      valueGetter: (params) =>
        countryData.find((country) => country.countryId === params.row.countryCode)?.countryCode ?? "",
    },
    {
      field: "currencyId",
      headerName: "Currency",
      flex: 0.3,
      valueGetter: (params) => currencyData.find((cur) => cur.currencyId === params.row.currencyId)?.isoCode ?? "",
    },
    {
      field: "invoiceVATAmountLocalCurrency",
      headerName: "Local VAT Amount",
      flex: 0.5,
      renderCell: (params) => (
        <div style={{ textAlign: "right", paddingRight: "5px" }}>
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
      align: "right",
    },
    {
      field: "fileId",
      headerName: "Invoice PDF available",
      flex: 0.5,
      renderCell: (params) =>
        params.value > 0 ? (
          <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} />
        ) : (
          <CheckCircleOutlineIcon style={{ color: "red", margin: "0 auto" }} />
        ),
    },
    {
      field: "InvoiceState",
      headerName: "Invoice State",
      flex: 0.6,
      renderCell: (params) => {
        const handleChange = (selectedKey) => {
          const rowData = params.row;
          const updatedRowData = { ...rowData, invoiceStateId: selectedKey };
          handleInvoiceStateUpdate(updatedRowData);
        };
        const selectDisabled = selectedClaimPreview.claimStateId === 16;
        const isPrefinancedInvoice = (params.row.preFinanceId ?? 0) > 0;
        const blockedVfStates = new Set(["NPY", "TNC", "NCL"]);
        let processedOptions = JSON.parse(JSON.stringify(invoiceStateData)).map((option) => {
          const shortCode = String(option.invoiceStateShortCode ?? "").toUpperCase();
          let disabledOption = selectedClaimPreview.claimStateId === 4 && [1, 2, 4].includes(option.invoiceStateId);
          if (isPrefinancedInvoice && blockedVfStates.has(shortCode)) {
            disabledOption = true;
          }
          return {
            ...option,
            key: option.invoiceStateId,
            value: option.invoiceStateShortCode,
            disabled: disabledOption,
          };
        });
        return (
          <SelectWrapperDB
            disabled={selectDisabled}
            name="invoiceState"
            rowIndex={params.api.getRowIndex(params.row.__rowNum__)}
            selectedKey={params.row.invoiceStateId}
            options={processedOptions}
            handleChange={handleChange}
          />
        );
      },
    },
    {
      field: "InvoiceLabel",
      headerName: "Invoice Label",
      flex: 0.6,
      renderCell: (params) => {
        const handleChange = (selectedKey) => {
          const rowData = params.row;
          const updatedRowData = { ...rowData, invoiceLabelId: selectedKey };
          handleInvoiceLabelUpdate(updatedRowData);
        };
        return (
          <SelectWrapperDB
            name="invoiceLabel"
            rowIndex={params.api.getRowIndex(params.row.__rowNum__)}
            selectedKey={params.row.invoiceLabelId}
            options={JSON.parse(
              JSON.stringify(JSON.parse(JSON.stringify(invoiceLabelData).split('"invoiceLabelId":').join('"key":')))
                .split('"invoiceLabelShortCode":')
                .join('"value":')
            )}
            handleChange={handleChange}
          />
        );
      },
    },
    {
      field: "excludeFromClaim",
      headerName: "Exclude from Claim",
      flex: 0.5,
      hide: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              onClick={(e) => {
                setViewCustomerInvoiceDialog({
                  isOpen: true,
                  downloadFile: true,
                  title: "Manage Invoice",
                  params: params.row,
                  custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                  productInfo: [
                    {
                      productCode: params.row.claimProductCode,
                      productSubCode: params.row.claimProductSubCode,
                    },
                  ],
                  subTitle: "Are you sure you want to manage " + params.row.contactName + "?",
                  handleFile: () => {
                    handleFile(updatedData);
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const FooterRow = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontWeight: "bold", marginRight: 10 }}>Total Net Amount:</div>
          <div>
            {gridTotalGrossAmount.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          <div style={{ fontWeight: "bold", marginLeft: 20, marginRight: 10 }}>Total VAT Amount:</div>
          <div>
            {gridTotalVATAmount.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
        <div>
          <GridFooter
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[15, 50, 100]}
          />
        </div>
      </div>
    );
  };

  var updatedData;
  const handleFile = (updatedData) => {};

  const getRowClassName = (params) => {
    const invoiceGrossAmount = params.row.invoiceGrossAmount;
    const invoiceStateId = params.row.invoiceStateId;
    if (invoiceStateId === 4) {
      return "highlight-row-removed";
    }
    if (invoiceStateId === 3) {
      return "highlight-row-ready-for-claim";
    }
    if (invoiceStateId === 2) {
      return "highlight-row-not-claimable";
    }
    if (invoiceGrossAmount > 3000) {
      return "highlight-row";
    }
    return "";
  };

  const handleBankAccountValue = (value, bankAccountId) => {
    setSelectedBankAccount(value);
  };

  const handleClaimStateValue = (value, name) => {
    setSelectedClaimStateId(value);
    if (value === 4) {
      setSelectedClaimDate(new Date().toISOString().slice(0, 10));
    }
    if (value === 16) {
      if (claimCodeSuffix === "00") {
        setSelectedNotificationDate(new Date().toISOString().slice(0, 10));
      } else {
        setSelectedReimbursementDate(new Date().toISOString().slice(0, 10));
      }
    }
  };

  const handleSelectedClaimDateValue = (event) => {
    setSelectedClaimDate(event.target.value);
  };
  const handleSelectedNotificationDateValue = (event) => {
    setSelectedNotificationDate(event.target.value);
  };
  const handleSelectedReimbursementDateValue = (event) => {
    setSelectedReimbursementDate(event.target.value);
  };
  const handleSelectedPaymentDateValue = (event) => {
    setSelectedPaymentDate(event.target.value);
  };
  const handleTaxOfficeRefNo = (event) => {
    setSelectedTaxOfficeRefNo(event.target.value);
  };

  const buildTaxOfficeRefNoInUseMessage = (conflictingClaimCode) => {
    if (conflictingClaimCode) {
      return `This reference number is already in use on claim ${conflictingClaimCode}.`;
    }

    return "This reference number is already in use.";
  };

  const checkTaxOfficeRefNoAvailability = useCallback(
    async (refNo) => {
      const trimmedRefNo = refNo?.trim();

      if (!trimmedRefNo) {
        setTaxOfficeRefNoError("");
        return false;
      }

      try {
        const response = await axios.get("/api/v1/claims/checkTaxOfficeRefNo", {
          params: {
            taxOfficeRefNo: trimmedRefNo,
            excludeClaimId: selectedClaimPreview?.claimId,
          },
        });

        if (response.data?.isInUse) {
          setTaxOfficeRefNoError(buildTaxOfficeRefNoInUseMessage(response.data?.conflictingClaimCode));
          return true;
        }

        setTaxOfficeRefNoError("");
        return false;
      } catch (error) {
        console.error("Error checking reference number availability:", error);
        setTaxOfficeRefNoError("");
        return false;
      }
    },
    [selectedClaimPreview?.claimId]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkTaxOfficeRefNoAvailability(selectedTaxOfficeRefNo);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [selectedTaxOfficeRefNo, checkTaxOfficeRefNoAvailability]);

  const getPreviewData = async () => {
    const claimId = selectedClaimId.current;
    if (!claimId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/api/v1/claims/claimView/" + claimId);
      // Ignore outdated responses when the user quickly opens another claim.
      if (selectedClaimId.current !== claimId) {
        return;
      }

      setSelectedClaimPreview(response.data);
      const claimStateId = response.data.claimStateId;
      const filteredInvoices = (response.data.customerInvoices || []).filter((invoice) => {
        const isClaimedByCurrent = invoice.claimId === response.data.claimId;
        const isUnclaimed = invoice.claimId === null || invoice.claimId === 0;

        if (claimStateId === 1) {
          return isClaimedByCurrent || isUnclaimed;
        }

        if (claimStateId === 3) {
          return isClaimedByCurrent && invoice.invoiceStateId === 3;
        }

        return isClaimedByCurrent && (invoice.invoiceStateId === 3 || invoice.invoiceStateId === 9);
      });
      setHasPreFinancedInvoices(filteredInvoices.some((invoice) => invoice.preFinanceId > 0));
      setPayableToDelta(response.data.claimCode?.endsWith("1"));
      setSelectedBankAccount(response.data.payableBankAccountId);
      setInvoiceGridData(filteredInvoices);
      setLoadedClaimId(claimId);
    } catch (error) {
      if (selectedClaimId.current !== claimId) {
        return;
      }
      console.error("Error loading claim view:", error);
      setLoadedClaimId(null);
    } finally {
      if (selectedClaimId.current === claimId) {
        setLoading(false);
      }
    }
  };

  const [entityData, setEntityData] = useState([]);
  useEffect(() => {
    if (!customerId) {
      return;
    }
    axios.get("/api/v1/entities/" + customerId).then((response) => setEntityData(response.data));
  }, [customerId]);
  const claimRefundCountryCode = useMemo(() => {
    const claimCountryId = viewClaimDialog.params?.countryId ?? selectedClaimPreview?.country;
    if (!claimCountryId) {
      return "";
    }

    return countryData.find((country) => Number(country.countryId) === Number(claimCountryId))?.countryCode || "";
  }, [countryData, selectedClaimPreview?.country, viewClaimDialog.params?.countryId]);
  const isNlEntity = isNlEntityCountry(entityData.countryId);
  const isClaimXmlValidationAvailable =
    isNlEntity && isEuVatRefundCountry(claimRefundCountryCode);
  const claimXmlValidationBlockedMessage = !isNlEntity
    ? CLAIM_XML_VALIDATION_ORIGIN_UNAVAILABLE_MESSAGE
    : !isEuVatRefundCountry(claimRefundCountryCode)
      ? CLAIM_XML_VALIDATION_UNAVAILABLE_MESSAGE
      : null;
  const isClaimXmlDownloadBlockedForNonEuRefund =
    isNlEntity && !isEuVatRefundCountry(claimRefundCountryCode);
  const hasClaimXmlDownloadEndpoint = hasClaimXmlDownloadForEntity(entityData.countryId);
  const isPolishTaxOfficeNumberMissing =
    Number(entityData.countryId) === POLAND_COUNTRY_ID &&
    !String(entityData.taxOfficeNumber ?? "").trim();
  const isGermanSteuerNummerNotFilled = isGermanSteuerNummerMissing(entityData, countryData || []);
  const canDownloadClaimXml =
    canWriteClaims &&
    !isClaimXmlDownloadBlockedForNonEuRefund &&
    hasClaimXmlDownloadEndpoint &&
    (isNlEntity ? !claimXmlValidation || canDownloadOrSubmitClaimXml : true);

  const promptTncConfirmation = (preview) =>
    new Promise((resolve) => {
      tncConfirmResolverRef.current = resolve;
      setTncConfirmDialog({ isOpen: true, preview });
    });

  const handleTncConfirmClose = (result) => {
    setTncConfirmDialog({ isOpen: false, preview: null });
    const resolve = tncConfirmResolverRef.current;
    tncConfirmResolverRef.current = null;
    if (resolve) {
      if (typeof result === "boolean") {
        resolve({ confirmed: result, convertVfToRfc: false });
        return;
      }

      resolve({
        confirmed: Boolean(result?.confirmed),
        convertVfToRfc: Boolean(result?.convertVfToRfc),
        keepNonVfInvoicesAsRfc: Boolean(result?.keepNonVfInvoicesAsRfc),
      });
    }
  };

  const getInvoiceStateIdByShortCode = (shortCode) =>
    invoiceStateData.find((state) => state.invoiceStateShortCode === shortCode)?.invoiceStateId;

  const applyVfNeedsRfcToGrid = (gridData, vfNeedsRfc, claimId) => {
    const rfcStateId = getInvoiceStateIdByShortCode("RFC") ?? 3;
    const vfIds = new Set(
      (vfNeedsRfc ?? []).map((invoice) => invoice.customerInvoiceId ?? invoice.CustomerInvoiceId)
    );

    return gridData.map((invoice) => {
      if (!vfIds.has(invoice.customerInvoiceId)) {
        return invoice;
      }

      return {
        ...invoice,
        invoiceStateId: rfcStateId,
        claimId,
      };
    });
  };

  const applyNonVfLockChangesToGrid = (gridData, nonVfToTnc, claimId, keepNonVfInvoicesAsRfc) => {
    const rfcStateId = getInvoiceStateIdByShortCode("RFC") ?? 3;
    const tncStateId = getInvoiceStateIdByShortCode("TNC") ?? 2;
    const rjtStateId = getInvoiceStateIdByShortCode("RJT");
    const nclStateId = getInvoiceStateIdByShortCode("NCL");
    const protectedStateIds = new Set([rjtStateId, nclStateId].filter(Boolean));
    const nonVfIds = new Set(
      (nonVfToTnc ?? []).map((invoice) => invoice.customerInvoiceId ?? invoice.CustomerInvoiceId)
    );

    return gridData.map((invoice) => {
      if (!nonVfIds.has(invoice.customerInvoiceId) || protectedStateIds.has(invoice.invoiceStateId)) {
        return invoice;
      }

      if (keepNonVfInvoicesAsRfc) {
        return {
          ...invoice,
          invoiceStateId: rfcStateId,
          claimId,
        };
      }

      return {
        ...invoice,
        invoiceStateId: tncStateId,
        claimId: 0,
      };
    });
  };

  const normalizeLockPreview = (preview) => ({
    ...preview,
    hasVfInvoices: preview?.hasVfInvoices ?? preview?.HasVfInvoices ?? false,
    wouldApplyAutomaticTnc: preview?.wouldApplyAutomaticTnc ?? preview?.WouldApplyAutomaticTnc ?? false,
    includedSummary: preview?.includedSummary ?? preview?.IncludedSummary ?? {},
    vfSummary: preview?.vfSummary ?? preview?.VfSummary ?? {},
    included: preview?.included ?? preview?.Included ?? [],
    vfNeedsRfc: preview?.vfNeedsRfc ?? preview?.VfNeedsRfc ?? [],
    nonVfToTnc: preview?.nonVfToTnc ?? preview?.NonVfToTnc ?? [],
    excluded: preview?.excluded ?? preview?.Excluded ?? [],
  });

  const handleUpdateClaim = async () => {
    if (!canWriteClaims) {
      return;
    }

    const isReferenceNumberInUse = await checkTaxOfficeRefNoAvailability(selectedTaxOfficeRefNo);
    if (isReferenceNumberInUse) {
      return;
    }

    const targetClaimStateId = parseInt(selectedClaimStateId, 10);
    const previousClaimStateId = Number(
      initialClaimStateIdRef.current ?? selectedClaimPreview.claimStateId ?? 0
    );

    if (
      Number.isFinite(targetClaimStateId) &&
      targetClaimStateId >= 3 &&
      Number.isFinite(previousClaimStateId) &&
      previousClaimStateId < 3
    ) {
      const bankAccountId = parseInt(selectedBankAccount || initPayableBankAccountId, 10);
      const hasBankAccount = Number.isFinite(bankAccountId) && bankAccountId > 0;
      if (!payableToDelta && !hasBankAccount) {
        setLockPaymentRequiredDialog({ isOpen: true });
        return;
      }

      try {
        const previewResponse = await axios.get(
          `/api/v1/claims/${selectedClaimPreview.claimId}/lockPreview`,
          { params: { targetClaimStateId } }
        );
        const preview = normalizeLockPreview(previewResponse.data);
        lockPreviewRef.current = preview;
        const confirmation = await promptTncConfirmation(preview);
        if (!confirmation?.confirmed) {
          lockPreviewRef.current = null;
          return;
        }

        lockPreviewRef.current = {
          ...preview,
          convertVfToRfc: Boolean(confirmation.convertVfToRfc),
          keepNonVfInvoicesAsRfc: Boolean(confirmation.keepNonVfInvoicesAsRfc),
        };
      } catch (error) {
        console.error("Error loading claim lock preview:", error);
        alert("Something went wrong while loading the claim lock overview.");
        lockPreviewRef.current = null;
        return;
      }
    }

    setUpdatingClaim(true);

    const confirmedPreview = lockPreviewRef.current;
    lockPreviewRef.current = null;
    let gridForUpdate = invoiceGridData.map((invoice) => ({ ...invoice }));
    if (confirmedPreview?.convertVfToRfc) {
      gridForUpdate = applyVfNeedsRfcToGrid(
        gridForUpdate,
        confirmedPreview.vfNeedsRfc ?? [],
        selectedClaimPreview.claimId
      );
    }

    if (
      confirmedPreview?.hasVfInvoices &&
      (confirmedPreview?.nonVfToTnc ?? []).length > 0
    ) {
      gridForUpdate = applyNonVfLockChangesToGrid(
        gridForUpdate,
        confirmedPreview.nonVfToTnc ?? [],
        selectedClaimPreview.claimId,
        Boolean(confirmedPreview.keepNonVfInvoicesAsRfc)
      );
    }

    const exchangeRate = countryData.find((country) => country.countryId === viewClaimDialog.params.countryId)?.exchangeRate || 1;
    const claimVATAmountEUR = totalVATAmount * exchangeRate;
    let claimArray = {
      claimCode: claimCode,
      paymentTypeId: selectedPaymentTypeId,
      claimStateId: selectedClaimStateId,
      liters: selectedLiters,
      advancedRefundAmountEUR: selectedAdvancedRefundAmountEUR,
      claimDate: selectedClaimDate,
      notificationDate: selectedNotificationDate,
      reimbursementDate: selectedReimbursementDate,
      payDate: selectedPaymentDate,
      PayableBankAccountId: selectedBankAccount,
      paymentToDelta: payableToDelta ? 1 : 0,
      taxOfficeRefNo: selectedTaxOfficeRefNo,
      claimGrossAmount: totalGrossAmount,
      claimVATAmountLocCur: totalVATAmount,
      claimVATAmountEUR: claimVATAmountEUR,
      msrDocuments: buildMsrDocumentsToSave().join(","),
      keepNonVfInvoicesAsRfc: Boolean(confirmedPreview?.keepNonVfInvoicesAsRfc),
    };

    const isLockedOrLater = parseInt(selectedClaimStateId, 10) >= 3;
    const claimId = selectedClaimPreview.claimId;

    gridForUpdate.forEach((invoice) => {
      if (isLockedOrLater) {
        invoice.claimId = invoice.invoiceStateId === 3 || invoice.invoiceStateId === 9 ? claimId : 0;
        return;
      }

      if (invoice.invoiceStateId === 2 || invoice.invoiceStateId === 4) {
        invoice.claimId = 0;
        return;
      }

      invoice.claimId = claimId;
    });

    try {
      await axios.put("/api/v1/customerInvoices/updateInvoiceEditFormBulk", gridForUpdate, {
        headers: { "Content-Type": "application/json" },
      });

      await axios.put("/api/v1/claims/claimViewUpdate/" + claimId, claimArray, {
        headers: { "Content-Type": "application/json" },
      });

      setDataVersion(dataVersion + 1);

      try {
        await axios.post("/api/v1/claims/updateInvoiceAmounts/" + claimId);
      } catch (error) {
        console.log("Update error!: " + error);
      }

      getPreviewData();
      await delay(1000);
      setUpdatingClaim(false);
      refreshTable();
    } catch (error) {
      console.error("Error updating claim:", error);
      const conflictData = error?.response?.status === 409 ? error?.response?.data : null;
      if (conflictData?.isInUse) {
        const conflictMessage = buildTaxOfficeRefNoInUseMessage(conflictData?.conflictingClaimCode);
        setTaxOfficeRefNoError(conflictMessage);
        alert(conflictMessage);
        setUpdatingClaim(false);
        throw error;
      }

      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      alert(apiMessage || "Something went wrong while updating the claim.");
      setUpdatingClaim(false);
      throw error; // Re-throw so calling functions can handle it
    }
  };

  const showClaimedUndo =
    Number(selectedClaimPreview.claimStateId) === 4 &&
    canUndoClaim &&
    !updatingClaim;
  const canUndoClaimed =
    showClaimedUndo &&
    !isUndoingClaimed &&
    Boolean(selectedClaimPreview.claimId);

  const handleRequestUndoClaimed = () => {
    if (!canUndoClaimed) {
      return;
    }

    setUndoClaimedError("");
    setOpenUndoClaimedConfirmation(true);
  };

  const handleCloseUndoClaimedConfirmation = async (confirmed) => {
    setOpenUndoClaimedConfirmation(false);
    if (!confirmed) {
      return;
    }

    await handleUndoClaimed();
  };

  const handleUndoClaimed = async () => {
    if (!canUndoClaimed) {
      return;
    }

    setIsUndoingClaimed(true);
    setUndoClaimedError("");

    try {
      await axios.post(`/api/v1/claims/${selectedClaimPreview.claimId}/undoClaimed`);
      setDataVersion(dataVersion + 1);
      await getPreviewData();
      if (typeof refreshTable === "function") {
        refreshTable();
      }
    } catch (requestError) {
      console.error("Unable to undo claimed status:", requestError);
      setUndoClaimedError(
        requestError.response?.data?.message ||
          "The claim could not be returned to Locked for claiming."
      );
    } finally {
      setIsUndoingClaimed(false);
    }
  };

  const handleRequestDeleteClaim = () => {
    if (!canWriteClaims || selectedClaimPreview.claimStateId !== 1) {
      return;
    }

    setOpenDeleteClaimConfirmation(true);
  };

  const handleCloseDeleteClaimConfirmation = async (confirmed) => {
    setOpenDeleteClaimConfirmation(false);
    if (!confirmed) {
      return;
    }

    await handleDeleteClaim();
  };

  const handleDeleteClaim = async () => {
    if (!canWriteClaims) {
      return;
    }

    let selectedClaimId = selectedClaimPreview.claimId;
    axios
      .delete("/api/v1/claims/" + selectedClaimId)
      .then((response) => {
        setDataVersion(dataVersion + 1);
        axios.put("/api/v1/customerInvoices/updateInvoiceRemoveClaimId/" + selectedClaimId).then((response) => {});
        setAlertInfo({
          message: "The claim was deleted successfully!",
          type: "success",
        });
        setTimeout(() => setAlertInfo({ message: "", type: "" }), 5000);
      })
      .catch((error) => {
        setAlertInfo({
          message: "Something went wrong while deleting the claim.",
          type: "error",
        });
        setTimeout(() => setAlertInfo({ message: "", type: "" }), 5000);
      });
    await delay(5000);
    handleCloseClaimDialog();
    props.refreshTable();
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleXMLDownload = async ({
    skipVatRequiredWarning = false,
    skipUncheckedDocumentsWarning = false,
    skipMissingPolishNawWarning = false,
    skipCesMissingWarning = false,
    skipTaxOfficeNumberWarning = false,
    skipSteuerNummerWarning = false,
  } = {}) => {
    if (isClaimXmlDownloadBlockedForNonEuRefund) {
      return;
    }

    const vatDocsAvailable = areAllVatRequiredDocumentsAvailable();

    if (!skipVatRequiredWarning && !vatDocsAvailable) {
      setVatRequiredDialog({ isOpen: true });
      return;
    }

    const uncheckedDocuments = getUncheckedAvailableMsrDocuments();
    if (!skipUncheckedDocumentsWarning && uncheckedDocuments.length > 0) {
      setUncheckedDocumentsDialog({ isOpen: true, documents: uncheckedDocuments });
      return;
    }

    const missingPolishNawFields = getMissingPolishXmlNawFields(entityData);
    if (!skipMissingPolishNawWarning && missingPolishNawFields.length > 0) {
      setMissingPolishNawDialog({ isOpen: true, fields: missingPolishNawFields });
      return;
    }

    if (isPreFinanceCesDocumentMissing && !skipCesMissingWarning) {
      setCesMissingDialog({ isOpen: true });
      return;
    }

    if (isPolishTaxOfficeNumberMissing && !skipTaxOfficeNumberWarning) {
      setTaxOfficeNumberDialog({ isOpen: true });
      return;
    }

    if (isGermanSteuerNummerNotFilled && !skipSteuerNummerWarning) {
      setSteuerNummerDialog({ isOpen: true });
      return;
    }

    try {
      await updateMSRDocuments();
      proceedWithXMLDownload();
    } catch (error) {
      console.error("Error updating MSR documents before XML download:", error);
      alert("Error saving MSR documents before XML download. Please try again.");
    }
  };

  const handleValidateClaimXml = async () => {
    if (!isClaimXmlValidationAvailable) {
      return;
    }

    const vatDocsAvailable = areAllVatRequiredDocumentsAvailable();
    if (!vatDocsAvailable) {
      setVatRequiredDialog({ isOpen: true });
      return;
    }

    try {
      setValidatingClaimXml(true);
      setClaimXmlValidation(null);
      setValidationOverride(false);

      await updateMSRDocuments();

      const claimId = selectedClaimPreview.claimId;
      const response = await axios.post("/api/v1/claims/validateClaimXml/" + claimId);
      setClaimXmlValidation(response.data);
      setValidationDialogOpen(true);
    } catch (error) {
      console.error("Error validating claim XML:", error);
      setResponseMessage(error.response?.data?.detail || "Something went wrong while validating the claim XML.");
      setShowPopup(true);
    } finally {
      setValidatingClaimXml(false);
    }
  };

  const proceedWithXMLDownload = () => {
    const getClaimUrl = getClaimXmlDownloadUrl(entityData.countryId, selectedClaimPreview.claimId);
    if (!getClaimUrl) {
      return;
    }

    // MSR documents are now saved in database, no need for query parameters
    const finalUrl = getClaimUrl;

    axios({
      url: finalUrl,
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        const contentDisposition = response.headers["content-disposition"];
        let filename = "file.zip";
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          let matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
        const file = new Blob([response.data], { type: "application/xml" });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = filename;
        link.click();
      })
      .catch((error) => {
        alert("Something went wrong while generating the XML file.");
      });
  };

  const handleXMLSendClaimToTaxAuthorities = async () => {
    setShowPopup(true);

    try {
      await updateMSRDocuments();

      let claimId = selectedClaimPreview.claimId;

      // MSR documents are now saved in database, no need for query parameters
      const finalUrl =
        "/api/v1/claims/sendClaimToTaxAuthority_NL/" + claimId + (validationOverride ? "?acknowledgeWarnings=true" : "");

      axios({
        url: finalUrl,
        method: "GET",
        responseType: "text",
      })
        .then((response) => {
          if (response.status === 200) {
            setResponseMessage("Upload successful");
          } else {
            setResponseMessage("Something went wrong.");
          }
        })
        .catch((error) => {
          if (error.response?.data?.issues) {
            setClaimXmlValidation(error.response.data);
            setValidationOverride(false);
            setValidationDialogOpen(true);
            setResponseMessage("");
            return;
          }

          setResponseMessage("Something went wrong while generating the XML file.");
        });
    } catch (error) {
      console.error("Error updating MSR documents before XML submit:", error);
      setResponseMessage("Error saving MSR documents before XML submit. Please try again.");
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleClickOpenConfirmation = () => {
    setOpenConfirmation(true);
  };

  const handleProceedWithValidationWarnings = () => {
    setValidationOverride(true);
  };

  const handleDownloadFromValidationDialog = () => {
    setValidationDialogOpen(false);
    handleXMLDownload();
  };

  const handleSubmitFromValidationDialog = () => {
    setValidationDialogOpen(false);
    handleClickOpenConfirmation();
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleChange = () => {
    setOpen(true);
  };

  const handleClose = (answer) => {
    if (answer) {
      if (hasPreFinancedInvoices) {
        setClaimCode(claimCode.slice(0, -2) + "10");
      } else {
        setClaimCode(claimCode.slice(0, -2) + "00");
      }
      setClaimCode((prevCode) => `${prevCode.slice(0, -1)}${payableToDelta ? "0" : "1"}`);
    }
    setPayableToDelta(!payableToDelta);
    setOpen(false);
  };

  const handleCloseCorrectionConfirmation = async (confirmed) => {
    setOpenCorrectionConfirmation(false);
    if (confirmed) {
      try {
        const response = await axios.post("/api/v1/claims/createCorrection/" + selectedClaimPreview.claimId);
        setViewClaimDialog({
          isOpen: true,
          title: "View Claim",
          params: response.data,
          custInfo: viewClaimDialog.custInfo,
        });
        refreshTable();
      } catch (error) {
        console.error("Error creating correction claim:", error);
        setResponseMessage(error.response?.data || "Error creating correction claim");
        setShowPopup(true);
      }
    }
  };

  // Check if a document type exists in customer documents for the specific claim country
  const isDocumentTypeAvailable = (documentTypeId) => {
    const claimCountryId = viewClaimDialog.params?.countryId;
    if (!claimCountryId) return false;

    return customerDocuments.some(
      (doc) =>
        Number(doc.documentType) === Number(documentTypeId) &&
        Number(doc.countryId) === Number(claimCountryId) &&
        Number(doc.deleted) === 0
    );
  };

  // Check if a document type is required for the specific claim country
  const isDocumentTypeRequiredForCountry = (doc) => {
    const claimCountryId = viewClaimDialog.params?.countryId;
    if (!claimCountryId) return false;

    // For VAT required documents
    if (doc.vatRequired) {
      if (doc.vatRequiredCountries) {
        const vatCountries = Array.isArray(doc.vatRequiredCountries)
          ? doc.vatRequiredCountries
          : doc.vatRequiredCountries.split(",").filter((id) => id.trim());
        return vatCountries.includes(claimCountryId.toString());
      }
      // If no vatRequiredCountries specified, use the old logic
      return true;
    }

    // For ED required documents
    if (doc.edRequired) {
      if (doc.edRequiredCountries) {
        const edCountries = Array.isArray(doc.edRequiredCountries)
          ? doc.edRequiredCountries
          : doc.edRequiredCountries.split(",").filter((id) => id.trim());
        return edCountries.includes(claimCountryId.toString());
      }
      // If no edRequiredCountries specified, use the old logic
      return true;
    }

    return false;
  };

  // Check if all VAT required documents are available
  const areAllVatRequiredDocumentsAvailable = () => {
    if (!documentTypes) return true;

    const claimCountryId = viewClaimDialog.params?.countryId;
    if (!claimCountryId) return true;

    const vatRequiredDocuments = documentTypes.filter((doc) => {
      if (!doc.vatRequired) return false;

      // Check if this country is in the vatRequiredCountries list
      if (doc.vatRequiredCountries) {
        const vatCountries = Array.isArray(doc.vatRequiredCountries)
          ? doc.vatRequiredCountries
          : doc.vatRequiredCountries.split(",").filter((id) => id.trim());
        // If country is NOT in the list, then it's NOT required for this country
        return vatCountries.includes(claimCountryId.toString());
      }

      // If no vatRequiredCountries specified, use the old logic
      return true;
    });

    return vatRequiredDocuments.every((doc) => isDocumentTypeAvailable(doc.documentTypeId));
  };

  const buildMsrDocumentsToSave = () => {
    const docs = [...selectedMSRDocuments];

    if (
      hasPreFinancedInvoices &&
      cesDocumentTypeId != null &&
      isDocumentTypeAvailable(cesDocumentTypeId) &&
      !docs.includes(cesDocumentTypeId)
    ) {
      docs.push(cesDocumentTypeId);
    }

    return docs;
  };

  const getUncheckedAvailableMsrDocuments = () => {
    if (!documentTypes?.length) {
      return [];
    }

    const selectedIds = new Set(buildMsrDocumentsToSave().map((id) => Number(id)));

    return documentTypes
      .filter((doc) => !doc.edRequired)
      .filter((doc) => isDocumentTypeAvailable(doc.documentTypeId))
      .filter((doc) => !selectedIds.has(Number(doc.documentTypeId)))
      .map((doc) => doc.name || `Document ${doc.documentTypeId}`);
  };

  // Handle MSR document selection
  const handleMSRDocumentChange = (documentTypeId, checked) => {
    if (!checked && hasPreFinancedInvoices && cesDocumentTypeId != null && documentTypeId === cesDocumentTypeId) {
      return;
    }
    if (checked) {
      setSelectedMSRDocuments((prev) => [...prev, documentTypeId]);
    } else {
      setSelectedMSRDocuments((prev) => prev.filter((id) => id !== documentTypeId));
    }
  };

  // Update MSR documents using specific endpoint
  const updateMSRDocuments = async () => {
    const claimId = selectedClaimPreview.claimId;

    if (!claimId) {
      throw new Error("No claim ID available");
    }

    const docsToSave = buildMsrDocumentsToSave();
    if (docsToSave.length === 0) {
      return;
    }

    const msrDocumentsUpdateDto = {
      msrDocuments: docsToSave.join(","),
    };

    await axios.put(`/api/v1/claims/${claimId}/msrDocuments`, msrDocumentsUpdateDto, {
      headers: { "Content-Type": "application/json" },
    });
  };

  const StatusMessage = () => {
    const uploadStatus = viewClaimDialog?.params?.uploadStatus;
    const claimSequenceNumber = viewClaimDialog?.params?.claimSequenceNumber;
    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
        {claimSequenceNumber > 0 && (
          <>
            {uploadStatus === "acknowledgement" && (
              <Box display="flex" alignItems="center" mr={2}>
                <Box width={15} height={15} bgcolor="#b2f2bb" mr={0.5} />
                <Typography variant="body2" style={{ fontSize: "0.8rem" }}>
                  Claim upload status OK
                </Typography>
              </Box>
            )}
            {uploadStatus === "" && (
              <Box display="flex" alignItems="center" mr={2}>
                <Box width={15} height={15} bgcolor="#a5d8ff" mr={0.5} />
                <Typography variant="body2" style={{ fontSize: "0.8rem" }}>
                  Claim upload in progress
                </Typography>
              </Box>
            )}
            {uploadStatus !== "acknowledgement" && uploadStatus !== "" && (
              <Box display="flex" alignItems="center">
                <Box width={15} height={15} bgcolor="#ffc9c9" mr={0.5} />
                <Typography variant="body2" style={{ fontSize: "0.8rem" }}>
                  Error while uploading claim
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <div>
      <Dialog open={open} onClose={() => handleClose(false)}>
        <DialogTitle>{"Change Claim Code"}</DialogTitle>
        <DialogContent>
          <DialogContentText>Do you want to change the Claim Code to reflect the change?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)} color="primary">
            No
          </Button>
          <Button onClick={() => handleClose(true)} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openConfirmation}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to submit the claim XML?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation} color="primary">
            No
          </Button>
          <Button
            onClick={() => {
              handleXMLSendClaimToTaxAuthorities();
              handleCloseConfirmation();
            }}
            color="primary"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {showPopup && (
        <div style={overlayStyle}>
          <div style={popupStyle}>
            {responseMessage ? (
              <>
                <p>{responseMessage}</p>
                <p>
                  <button
                    onClick={handleClosePopup}
                    style={{ border: "none", background: "none", textDecoration: "underline", cursor: "pointer" }}
                  >
                    Close
                  </button>
                </p>
              </>
            ) : (
              <p>Please wait, upload in progress...</p>
            )}
          </div>
        </div>
      )}
      <ClaimXmlValidationDialog
        open={validationDialogOpen}
        validation={claimXmlValidation}
        canDownload={canDownloadClaimXml}
        canSubmit={entityData?.countryId === 1 && canDownloadOrSubmitClaimXml && canSubmitClaimXml}
        onClose={() => setValidationDialogOpen(false)}
        onProceedAnyway={handleProceedWithValidationWarnings}
        onDownload={handleDownloadFromValidationDialog}
        onSubmit={entityData.countryId === 1 ? handleSubmitFromValidationDialog : null}
      />
      <Backdrop
        open={showClaimLoading}
        onClick={handleCloseClaimDialog}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontSize: "18px" }}>Loading claim...</Typography>
      </Backdrop>
      <Dialog
        open={Boolean(viewClaimDialog.isOpen && isClaimDataReady)}
        onClose={handleCloseClaimDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="xl"
        keepMounted={false}
        TransitionProps={{ unmountOnExit: true }}
      >
        <DialogTitle id="alert-dialog-title">{viewClaimDialog.title}</DialogTitle>
        <Divider />
        <DialogContent>
          <Card mb={6}>
            <CardContent>
              {alertInfo.message && (
                <Alert severity={alertInfo.type} my={3}>
                  {alertInfo.message}
                </Alert>
              )}
              {updatingClaim ? (
                <Grid container xs={12} sx={{ mb: 5 }} justifyContent="center" alignItems="center" height={890}>
                  <Typography sx={{ fontSize: "21px", mt: 2 }} align="center">
                    <CircularProgress size={30} sx={{ mr: 2 }} />
                    Updating, please wait!
                  </Typography>
                </Grid>
              ) : (
                <div>
                  <Grid container xs={12} sx={{ mb: 5 }}>
                    <Grid container xs={6} spacing={1} sx={{ pr: 2 }}>
                      <Grid item xs={5}>
                        <Typography>Claim Code:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {claimCode}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Reference number:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          type="string"
                          value={selectedTaxOfficeRefNo}
                          onChange={handleTaxOfficeRefNo}
                          onBlur={() => checkTaxOfficeRefNoAvailability(selectedTaxOfficeRefNo)}
                          error={Boolean(taxOfficeRefNoError)}
                          helperText={taxOfficeRefNoError}
                          FormHelperTextProps={{ sx: { marginLeft: 0, textAlign: "left" } }}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-input": { padding: 1.0 },
                            "& .MuiFormHelperText-root": { marginLeft: 0, textAlign: "left" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Year:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {year}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Period:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {`${startMonth.toString().padStart(2, "0")} / ${endMonth.toString().padStart(2, "0")}`}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Country:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {countryData.find((country1) => country1.countryId === country)?.countryCode}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Claim State:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              "& .MuiFormControl-root": {
                                minWidth: 0,
                                width: "100%",
                              },
                            }}
                          >
                            <SelectWrapper
                              disabled={selectedClaimPreview.claimStateId === 4}
                              name="claimStateId"
                              label="Claim State"
                              options={JSON.parse(
                                JSON.stringify(JSON.parse(JSON.stringify(claimStateData).split('"claimStateId":').join('"key":')))
                                  .split('"description":')
                                  .join('"value":')
                              )}
                              handleChange={handleClaimStateValue}
                              value={selectedClaimStateId ? selectedClaimStateId : claimStateId}
                              selectedClaimPreviewClaimStateId={selectedClaimPreview.claimStateId}
                              enableSpecialBehavior={true}
                            />
                          </Box>
                          {showClaimedUndo ? (
                            <Button
                              type="button"
                              variant="text"
                              color="warning"
                              size="small"
                              onClick={handleRequestUndoClaimed}
                              disabled={!canUndoClaimed}
                              aria-label="Undo claimed status"
                              sx={{
                                flexShrink: 0,
                                minWidth: "auto",
                                px: 0.5,
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                                "&:hover": {
                                  textDecoration: "underline",
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              {isUndoingClaimed ? (
                                <CircularProgress size={16} color="warning" />
                              ) : (
                                "Undo"
                              )}
                            </Button>
                          ) : null}
                        </Box>
                        {undoClaimedError ? (
                          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                            {undoClaimedError}
                          </Typography>
                        ) : null}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Claim Date:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          disabled={selectedClaimPreview.claimStateId === 4}
                          type="date"
                          value={selectedClaimDate}
                          onChange={handleSelectedClaimDateValue}
                          helperText={dateFieldWarnings.claimDate.join(" ") || undefined}
                          FormHelperTextProps={DATE_WARNING_HELPER_PROPS}
                          inputProps={{ "aria-label": "Claim Date" }}
                          sx={{
                            "& .MuiOutlinedInput-input": { padding: 1.0 },
                            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& .MuiOutlinedInput-adornedEnd .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& input[type='date']::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Notification Date:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          type="date"
                          sx={{
                            "& .MuiOutlinedInput-input": { padding: 1.0 },
                            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& .MuiOutlinedInput-adornedEnd .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& input[type='date']::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }}
                          onChange={handleSelectedNotificationDateValue}
                          value={selectedNotificationDate}
                          helperText={dateFieldWarnings.notificationDate.join(" ") || undefined}
                          FormHelperTextProps={DATE_WARNING_HELPER_PROPS}
                          inputProps={{ "aria-label": "Notification Date" }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Reimbursement Date:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          type="date"
                          disabled={claimCodeSuffix === "00"}
                          sx={{
                            "& .MuiOutlinedInput-input": { padding: 1.0 },
                            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& .MuiOutlinedInput-adornedEnd .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& input[type='date']::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }}
                          onChange={handleSelectedReimbursementDateValue}
                          value={selectedReimbursementDate}
                          helperText={dateFieldWarnings.reimbursementDate.join(" ") || undefined}
                          FormHelperTextProps={DATE_WARNING_HELPER_PROPS}
                          inputProps={{ "aria-label": "Reimbursement Date" }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Pay Date:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          type="date"
                          disabled={claimCodeSuffix === "00"}
                          sx={{
                            "& .MuiOutlinedInput-input": { padding: 1.0 },
                            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& .MuiOutlinedInput-adornedEnd .MuiSvgIcon-root": {
                              color: "text.primary !important",
                            },
                            "& input[type='date']::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }}
                          onChange={handleSelectedPaymentDateValue}
                          value={selectedPaymentDate}
                          helperText={dateFieldWarnings.payDate.join(" ") || undefined}
                          FormHelperTextProps={DATE_WARNING_HELPER_PROPS}
                          inputProps={{ "aria-label": "Pay Date" }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>Payable Bank Account:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={hasPreFinancedInvoices && payableToDelta}
                              checked={payableToDelta}
                              onChange={handleChange}
                              name="payableToDelta"
                              color="primary"
                            />
                          }
                          label={
                            <div>
                              Payment to Delta
                              {hasPreFinancedInvoices && payableToDelta && (
                                <div style={{ fontSize: "small" }}>(has pre-financed invoices)</div>
                              )}
                              {payableToDelta && (
                                <div style={{ fontSize: "small" }}>
                                  IBAN: {hasPreFinancedInvoices ? deltaPayableIbanPreFinanced : deltaPayableIbanStandard}
                                </div>
                              )}
                            </div>
                          }
                        />
                        {console.log("payableToDelta", payableToDelta)}
                        {!payableToDelta && (
                          <BankAccountSelectBox
                            name="bankAccountId"
                            label="Bank Account"
                            options={(() => {
                              const hasDefault = bankAccountsFiltered.some((ba) => ba.isDefault);
                              return bankAccountsFiltered.map((ba) => ({
                                key: ba.bankAccountId,
                                value: ba.iban,
                                disabled: hasDefault && !ba.isDefault,
                              }));
                            })()}
                            handleChange={handleBankAccountValue}
                            value={selectedBankAccount ? selectedBankAccount : initPayableBankAccountId}
                            enableSpecialBehavior={true}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <Grid item xs={12} sx={{ p: 5, pt: 3 }} backgroundColor="#376fd0">
                        <Grid container spacing={20}>
                          {/* Financial Summary - Left side */}
                          <Grid item xs={6}>
                            <Grid container>
                              <Grid item xs={8}>
                                <Typography sx={{ fontSize: 18, color: "white" }}>Invoice Count:</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>{invoiceCount}</Typography>
                              </Grid>
                            </Grid>
                            <Grid container>
                              <Grid item xs={8}>
                                <Typography sx={{ fontSize: 18, color: "white" }}>Total Net Amount:</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>
                                  {(totalGrossAmount &&
                                    totalGrossAmount.toLocaleString("nl-NL", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })) ||
                                    "0,00"}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid container>
                              <Grid item xs={8}>
                                <Typography sx={{ fontSize: 18, color: "white" }}>Total VAT Amount:</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>
                                  {(totalVATAmount &&
                                    totalVATAmount.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) ||
                                    "0,00"}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* MSR Documents Selection - Right side */}
                          <Grid item xs={6}>
                            <Typography sx={{ fontSize: 16, color: "white", mb: 1, fontWeight: "bold" }}>MSR Documents:</Typography>
                            <Box sx={{ maxHeight: 200, overflowY: "auto", pr: 1 }}>
                              {documentTypes &&
                                documentTypes
                                  .filter((doc) => !doc.edRequired)
                                  .map((doc) => {
                                    const isCesRow =
                                      cesDocumentTypeId != null && Number(doc.documentTypeId) === Number(cesDocumentTypeId);
                                    const cesPrefinanceMissing =
                                      hasPreFinancedInvoices && isCesRow && !isDocumentTypeAvailable(doc.documentTypeId);
                                    const showMsrMissingRed =
                                      (isDocumentTypeRequiredForCountry(doc) && !isDocumentTypeAvailable(doc.documentTypeId)) ||
                                      cesPrefinanceMissing;
                                    const documentAvailable = isDocumentTypeAvailable(doc.documentTypeId);
                                    const checkboxDisabled =
                                      !documentAvailable ||
                                      (hasPreFinancedInvoices && isCesRow);
                                    const checkboxChecked =
                                      isCesRow && hasPreFinancedInvoices
                                        ? documentAvailable
                                        : selectedMSRDocuments.includes(doc.documentTypeId);

                                    return (
                                    <Box
                                      key={doc.documentTypeId}
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        mb: 0.5,
                                      }}
                                    >
                                      <Checkbox
                                        checked={checkboxChecked}
                                        onChange={(e) => handleMSRDocumentChange(doc.documentTypeId, e.target.checked)}
                                        disabled={checkboxDisabled}
                                        sx={{
                                          color: showMsrMissingRed ? "#ff6b6b" : "white",
                                          "&.Mui-checked": {
                                            color: showMsrMissingRed ? "#ff6b6b" : "white",
                                          },
                                          "&.Mui-disabled": {
                                            color: showMsrMissingRed ? "rgba(255, 107, 107, 0.5)" : "rgba(255, 255, 255, 0.3)",
                                          },
                                          "& .MuiSvgIcon-root": { fontSize: 14 },
                                          p: 0.5,
                                          mr: 0.5,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: showMsrMissingRed ? "#ff6b6b !important" : "white !important",
                                          fontWeight: isDocumentTypeRequiredForCountry(doc) ? "bold" : "normal",
                                          lineHeight: 1.3,
                                          flex: 1,
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {doc.name}
                                        {isDocumentTypeRequiredForCountry(doc) && " (VAT Required)"}
                                        {!isDocumentTypeAvailable(doc.documentTypeId) && " (N/A)"}
                                      </Typography>
                                    </Box>
                                    );
                                  })}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* Historic Claims Dropdown en Button */}
                      <Box display="flex" alignItems="center" mb={2} mt={2}>
                        {historicOptions && (
                          <SelectWrapper
                            name="historicClaim"
                            label="Historic Claims"
                            options={historicOptions}
                            value={dropdownValue}
                            handleChange={(value) => {
                              if (historicClaims.length > 0) {
                                setSelectedHistoricClaimId(value);
                              }
                            }}
                          />
                        )}
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{ ml: 2 }}
                          disabled={
                            !selectedHistoricClaimId || selectedHistoricClaimId === "default" || selectedHistoricClaimId === "none"
                          }
                          onClick={() => {
                            const selectedClaim = historicClaims.find(
                              (c) => c.historicClaimId.toString() === selectedHistoricClaimId.toString()
                            );
                            if (selectedClaim) {
                              setViewHistoricClaimDialog({
                                isOpen: true,
                                title: "Historic Claim " + selectedClaim.claimCode,
                                params: selectedClaim,
                              });
                            }
                          }}
                        >
                          View Historic Claim
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={selectedClaimPreview.claimStateId <= 3}
                          onClick={() => setOpenCorrectionConfirmation(true)}
                          sx={{ marginLeft: 2 }}
                        >
                          Create Correction Claim
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                      <StatusMessage />
                      <ClickAwayListener
                        onClickAway={() => {
                          handleCloseClaimInvoiceDownloadMenu();
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                          <IconButton
                            ref={claimInvoiceDownloadButtonRef}
                            color="primary"
                            aria-label="download claim invoices"
                            disabled={loading || invoiceGridData.length <= 0 || isClaimInvoiceDownloadInProgress}
                            onMouseEnter={handleOpenClaimInvoiceDownloadMenu}
                          >
                            {isClaimInvoiceDownloadInProgress ? <CircularProgress size={20} /> : <DownloadIcon />}
                          </IconButton>
                          <Popper
                            anchorEl={claimInvoiceDownloadMenuAnchorEl}
                            open={Boolean(claimInvoiceDownloadMenuAnchorEl)}
                            placement="bottom-start"
                            sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                            modifiers={[
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 8],
                                },
                              },
                            ]}
                          >
                            <Paper ref={claimInvoiceDownloadMenuPaperRef} onMouseLeave={handleClaimInvoiceDownloadMenuMouseLeave}>
                            <MenuList autoFocusItem={false} aria-label="download claim invoice options">
                            <MenuItem
                              onMouseEnter={handleCloseClaimInvoiceDownloadSubMenu}
                              onClick={() =>
                                startClaimInvoiceDownload({
                                  mode: CLAIM_INVOICE_DOWNLOAD_MODE.AllSeparate,
                                })
                              }
                              disabled={loading || invoiceGridData.length <= 0 || isClaimInvoiceDownloadInProgress}
                            >
                              Download all invoices
                            </MenuItem>
                            <MenuItem
                              onMouseEnter={handleCloseClaimInvoiceDownloadSubMenu}
                              onClick={() =>
                                startClaimInvoiceDownload({
                                  mode: CLAIM_INVOICE_DOWNLOAD_MODE.MergedSinglePdf,
                                })
                              }
                              disabled={loading || invoiceGridData.length <= 0 || isClaimInvoiceDownloadInProgress}
                            >
                              Download all invoices merged
                            </MenuItem>
                            <MenuItem
                              onMouseEnter={(e) =>
                                handleOpenClaimInvoiceDownloadSubMenu(e, CLAIM_INVOICE_DOWNLOAD_MODE.SortedByVatAmount)
                              }
                              disabled={loading || invoiceGridData.length <= 0 || isClaimInvoiceDownloadInProgress}
                            >
                              Download all invoices VAT sorted
                            </MenuItem>
                            <MenuItem
                              onMouseEnter={(e) =>
                                handleOpenClaimInvoiceDownloadSubMenu(
                                  e,
                                  CLAIM_INVOICE_DOWNLOAD_MODE.SortedByInvoiceDate
                                )
                              }
                              disabled={loading || invoiceGridData.length <= 0 || isClaimInvoiceDownloadInProgress}
                            >
                              Download all invoices Date sorted
                            </MenuItem>
                          </MenuList>
                            </Paper>
                          </Popper>
                          <Popper
                            anchorEl={claimInvoiceDownloadSubMenuAnchorEl}
                            open={Boolean(claimInvoiceDownloadSubMenuAnchorEl)}
                            placement="right-start"
                            sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
                            modifiers={[
                              {
                                name: "offset",
                                options: {
                                  offset: [8, 0],
                                },
                              },
                            ]}
                          >
                          <Paper ref={claimInvoiceDownloadSubMenuPaperRef} onMouseLeave={handleClaimInvoiceDownloadSubMenuMouseLeave}>
                            <MenuList autoFocusItem={false} aria-label="download claim invoice sort direction options">
                            <MenuItem
                              onClick={() =>
                                startClaimInvoiceDownload({
                                  mode: claimInvoiceDownloadSubMenuMode,
                                  sortDirection: CLAIM_INVOICE_DOWNLOAD_SORT_DIRECTION.Ascending,
                                })
                              }
                              disabled={!claimInvoiceDownloadSubMenuMode || isClaimInvoiceDownloadInProgress}
                            >
                              Asc
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                startClaimInvoiceDownload({
                                  mode: claimInvoiceDownloadSubMenuMode,
                                  sortDirection: CLAIM_INVOICE_DOWNLOAD_SORT_DIRECTION.Descending,
                                })
                              }
                              disabled={!claimInvoiceDownloadSubMenuMode || isClaimInvoiceDownloadInProgress}
                            >
                              Desc
                            </MenuItem>
                          </MenuList>
                        </Paper>
                        </Popper>
                        </Box>
                      </ClickAwayListener>
                    </Grid>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: 10,
                        backgroundColor: "#376fd0",
                        color: "white",
                      }}
                    >
                      <Typography style={{ marginLeft: "auto" }}>
                        {claimXmlValidation && (
                          <Typography component="span" sx={{ color: "white", mr: 2 }}>
                            XML validated {claimXmlValidation.hasErrors ? "(errors)" : claimXmlValidation.hasWarnings && !validationOverride ? "(warnings)" : "(ok)"}
                          </Typography>
                        )}
                        <Box
                          component="span"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mr: 0.5 }}
                        >
                          <Button
                            disabled={
                              !isClaimXmlValidationAvailable ||
                              validatingClaimXml ||
                              selectedClaimPreview.claimStateId !== 3 ||
                              !canSubmitClaimXml
                            }
                            varianrt="text"
                            onClick={handleValidateClaimXml}
                            aria-label="Validate claim XML"
                            sx={{
                              color: "white",
                              textDecoration: "underline",
                              "&.Mui-disabled": {
                                color: "rgba(255, 255, 255, 0.45)",
                              },
                            }}
                          >
                            {validatingClaimXml ? "Validating..." : "Validate claim XML"}
                          </Button>
                          {claimXmlValidationBlockedMessage && (
                            <Tooltip title={claimXmlValidationBlockedMessage} arrow placement="top">
                              <InfoOutlinedIcon
                                aria-label={claimXmlValidationBlockedMessage}
                                tabIndex={0}
                                sx={{ fontSize: 18, color: "rgba(255, 255, 255, 0.85)", cursor: "help" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box
                          component="span"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mr: 0.5 }}
                        >
                          <Button
                            disabled={selectedClaimPreview.claimStateId !== 3 || !canDownloadClaimXml}
                            varianrt="text"
                            onClick={() => handleXMLDownload()}
                            aria-label="Download claim XML"
                            sx={{
                              color: "white",
                              textDecoration: "underline",
                              "&.Mui-disabled": {
                                color: "rgba(255, 255, 255, 0.45)",
                              },
                            }}
                          >
                            Download claim XML
                          </Button>
                          {isClaimXmlDownloadBlockedForNonEuRefund && (
                            <Tooltip title={CLAIM_XML_DOWNLOAD_UNAVAILABLE_MESSAGE} arrow placement="top">
                              <InfoOutlinedIcon
                                aria-label={CLAIM_XML_DOWNLOAD_UNAVAILABLE_MESSAGE}
                                tabIndex={0}
                                sx={{ fontSize: 18, color: "rgba(255, 255, 255, 0.85)", cursor: "help" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {entityData.countryId === 1 && (
                          <Button
                            disabled={
                              selectedClaimPreview.claimStateId === 3
                                ? !areAllVatRequiredDocumentsAvailable() ||
                                  isPreFinanceCesDocumentMissing ||
                                  !canDownloadOrSubmitClaimXml ||
                                  !canSubmitClaimXml
                                : true
                            }
                            varianrt="text"
                            onClick={handleClickOpenConfirmation}
                            sx={{ color: "white", textDecoration: "underline" }}
                          >
                            Submit claim XML
                          </Button>
                        )}
                      </Typography>
                    </div>
                    <ViewCustomerInvoiceDialog
                      key={viewCustomerInvoiceDialog.params?.customerInvoiceId || Date.now()}
                      viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
                      setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
                      countryArr={countryData}
                      supplierArr={supplierData}
                      currencyArr={currencyData}
                      productTypeArr={productData}
                      invoiceStateArr={invoiceStateData}
                      invoiceLabelArr={invoiceLabelData}
                      refreshGrid={getPreviewData}
                    />
                    {loading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "500px",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    ) : (
                      <DataGrid
                        getRowId={(row) => row.customerInvoiceId}
                        disableSelectionOnClick
                        disableColumnSelector
                        components={{ Footer: FooterRow, Toolbar: GridToolbar }}
                        density="compact"
                        componentsProps={{
                          toolbar: {
                            csvOptions: { disableToolbarButton: false },
                            printOptions: { disableToolbarButton: false },
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 250 },
                          },
                        }}
                        pagination
                        paginationMode="client"
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        rowsPerPageOptions={[15, 50, 100]}
                        rows={invoiceGridData}
                        columns={columns}
                        ref={gridRef}
                        getRowClassName={getRowClassName}
                        sx={{
                          height: 500,
                          width: "100%",
                          borderRadius: 1,
                          backgroundColor: "background.paper",
                          boxShadow: 2,
                          "& .MuiDataGrid-cell:hover": { color: "primary.main" },
                          "& .MuiDataGrid-row:hover": {
                            "& .highlight-row .MuiDataGrid-cell": {
                              color: getHoverTextColor(theme),
                            },
                            "& .highlight-row-removed .MuiDataGrid-cell": {
                              color: getHoverTextColor(theme),
                            },
                            "& .highlight-row-ready-for-claim .MuiDataGrid-cell": {
                              color: getHoverTextColor(theme),
                            },
                            "& .highlight-row-not-claimable .MuiDataGrid-cell": {
                              color: getHoverTextColor(theme),
                            },
                          },
                          marginTop: 0,
                          ".highlight-row": { bgcolor: "#ffeeba", color: "#000000 !important" },
                          ".highlight-row-removed": { bgcolor: "#fae7e7", color: "#000000 !important" },
                          ".highlight-row-ready-for-claim": { bgcolor: "#c8e6c9", color: "#000000 !important" },
                          ".highlight-row-not-claimable": { bgcolor: "#d7ccc8", color: "#000000 !important" },
                        }}
                      />
                    )}
                  </Grid>
                </div>
              )}
              <Grid item container justifyContent="space-between" alignItems="center" sx={{ mt: 2.5 }}>
                <Grid item>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateClaim}
                    disabled={isUpdateDisabled}
                    aria-label="Update claim"
                  >
                    Update Claim
                    {updatingClaim && <CircularProgress size={20} style={{ marginLeft: 10, color: "white" }} />}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleCloseClaimDialog}
                    sx={{ marginLeft: 2 }}
                  >
                    Close
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRequestDeleteClaim}
                    aria-label="Delete claim"
                    sx={{ marginLeft: 2 }}
                    disabled={selectedClaimPreview.claimStateId !== 1 || !canWriteClaims}
                  >
                    Delete
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
      {viewHistoricClaimDialog.isOpen && (
        <ViewHistoricClaimDialog viewClaimDialog={viewHistoricClaimDialog} setViewHistoricClaimDialog={setViewHistoricClaimDialog} />
      )}
      <ClaimLockTncConfirmDialog
        isOpen={tncConfirmDialog.isOpen}
        preview={tncConfirmDialog.preview}
        onConfirm={handleTncConfirmClose}
        onCancel={handleTncConfirmClose}
        isBusy={false}
      />
      <Dialog
        open={openCorrectionConfirmation}
        onClose={() => handleCloseCorrectionConfirmation(false)}
        aria-labelledby="correction-confirmation-dialog-title"
        aria-describedby="correction-confirmation-dialog-description"
      >
        <DialogTitle id="correction-confirmation-dialog-title">{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="correction-confirmation-dialog-description">
            Are you sure you want to create a correction claim?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseCorrectionConfirmation(false)} color="primary">
            No
          </Button>
          <Button onClick={() => handleCloseCorrectionConfirmation(true)} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteClaimConfirmation}
        onClose={() => handleCloseDeleteClaimConfirmation(false)}
        aria-labelledby="delete-claim-confirmation-dialog-title"
        aria-describedby="delete-claim-confirmation-dialog-description"
      >
        <DialogTitle id="delete-claim-confirmation-dialog-title">{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-claim-confirmation-dialog-description">
            Are you sure you want to delete
            {selectedClaimPreview.claimCode ? ` ${selectedClaimPreview.claimCode}` : " this claim"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleCloseDeleteClaimConfirmation(false)}
            color="primary"
            aria-label="Cancel delete claim"
          >
            No
          </Button>
          <Button
            onClick={() => handleCloseDeleteClaimConfirmation(true)}
            color="primary"
            autoFocus
            aria-label="Confirm delete claim"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openUndoClaimedConfirmation}
        onClose={() => handleCloseUndoClaimedConfirmation(false)}
        aria-labelledby="undo-claimed-confirmation-dialog-title"
        aria-describedby="undo-claimed-confirmation-dialog-description"
      >
        <DialogTitle id="undo-claimed-confirmation-dialog-title">{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="undo-claimed-confirmation-dialog-description">
            Are you sure you want to undo the Claimed status
            {selectedClaimPreview.claimCode ? ` for ${selectedClaimPreview.claimCode}` : ""}? The claim will
            return to Locked for claiming.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleCloseUndoClaimedConfirmation(false)}
            color="primary"
            disabled={isUndoingClaimed}
            aria-label="Cancel undo claimed status"
          >
            No
          </Button>
          <Button
            onClick={() => handleCloseUndoClaimedConfirmation(true)}
            color="primary"
            autoFocus
            disabled={isUndoingClaimed}
            aria-label="Confirm undo claimed status"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* VAT Required Documents Missing Dialog */}
      <Dialog
        open={vatRequiredDialog.isOpen}
        onClose={() => setVatRequiredDialog({ isOpen: false })}
        aria-labelledby="vat-required-dialog-title"
        aria-describedby="vat-required-dialog-description"
      >
        <DialogTitle id="vat-required-dialog-title">{"VAT Required Documents Missing"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="vat-required-dialog-description">
            Not all VAT required documents are present. Please ensure all required documents are uploaded before proceeding.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVatRequiredDialog({ isOpen: false })} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setVatRequiredDialog({ isOpen: false });
              handleXMLDownload({ skipVatRequiredWarning: true });
            }}
            color="primary"
            autoFocus
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uncheckedDocumentsDialog.isOpen}
        onClose={() => setUncheckedDocumentsDialog({ isOpen: false, documents: [] })}
        aria-labelledby="unchecked-documents-dialog-title"
        aria-describedby="unchecked-documents-dialog-description"
      >
        <DialogTitle id="unchecked-documents-dialog-title">Available documents not selected</DialogTitle>
        <DialogContent>
          <DialogContentText id="unchecked-documents-dialog-description" component="div">
            Not all available documents are checked on this claim. Unchecked documents will not be added to the XML or the
            ZIP. Check them on the claim if they should be included.
            <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
              {uncheckedDocumentsDialog.documents.map((documentName) => (
                <li key={documentName}>{documentName}</li>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUncheckedDocumentsDialog({ isOpen: false, documents: [] })}
            color="primary"
            aria-label="Cancel XML download"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setUncheckedDocumentsDialog({ isOpen: false, documents: [] });
              handleXMLDownload({
                skipVatRequiredWarning: true,
                skipUncheckedDocumentsWarning: true,
              });
            }}
            color="primary"
            autoFocus
            aria-label="Continue XML download without unchecked documents"
          >
            Continue download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={missingPolishNawDialog.isOpen}
        onClose={() => setMissingPolishNawDialog({ isOpen: false, fields: [] })}
        aria-labelledby="missing-polish-naw-dialog-title"
        aria-describedby="missing-polish-naw-dialog-description"
      >
        <DialogTitle id="missing-polish-naw-dialog-title">Customer address fields missing</DialogTitle>
        <DialogContent>
          <DialogContentText id="missing-polish-naw-dialog-description" component="div">
            Some customer card fields used in the Polish XML are empty. Fill them in if they should be included, or
            continue the download without those XML tags.
            <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
              {missingPolishNawDialog.fields.map((fieldName) => (
                <li key={fieldName}>{fieldName}</li>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMissingPolishNawDialog({ isOpen: false, fields: [] })}
            color="primary"
            aria-label="Cancel XML download"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setMissingPolishNawDialog({ isOpen: false, fields: [] });
              handleXMLDownload({
                skipVatRequiredWarning: true,
                skipUncheckedDocumentsWarning: true,
                skipMissingPolishNawWarning: true,
              });
            }}
            color="primary"
            autoFocus
            aria-label="Continue XML download with missing customer address fields"
          >
            Continue download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lockPaymentRequiredDialog.isOpen}
        onClose={() => setLockPaymentRequiredDialog({ isOpen: false })}
        aria-labelledby="lock-payment-required-dialog-title"
        aria-describedby="lock-payment-required-dialog-description"
      >
        <DialogTitle id="lock-payment-required-dialog-title">Payment details required</DialogTitle>
        <DialogContent>
          <DialogContentText id="lock-payment-required-dialog-description">
            To lock this claim, select a bank account or check Payment to Delta.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLockPaymentRequiredDialog({ isOpen: false })}
            color="primary"
            autoFocus
            aria-label="Close payment details required dialog"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cessie mededeling missing — waarschuwing, download wel toestaan */}
      <Dialog
        open={cesMissingDialog.isOpen}
        onClose={() => setCesMissingDialog({ isOpen: false })}
        aria-labelledby="ces-missing-dialog-title"
        aria-describedby="ces-missing-dialog-description"
      >
        <DialogTitle id="ces-missing-dialog-title">{"Cessie mededeling ontbreekt"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="ces-missing-dialog-description">
            De cessie mededeling ontbreekt voor deze prefinancieringsclaim. De download bevat dit document niet. Upload het
            document alsnog voordat u de claim indient.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCesMissingDialog({ isOpen: false })} color="primary">
            Annuleren
          </Button>
          <Button
            onClick={() => {
              setCesMissingDialog({ isOpen: false });
              handleXMLDownload({
                skipVatRequiredWarning: true,
                skipUncheckedDocumentsWarning: true,
                skipMissingPolishNawWarning: true,
                skipCesMissingWarning: true,
              });
            }}
            color="primary"
            autoFocus
          >
            Toch downloaden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={taxOfficeNumberDialog.isOpen}
        onClose={() => setTaxOfficeNumberDialog({ isOpen: false })}
        aria-labelledby="tax-office-number-dialog-title"
        aria-describedby="tax-office-number-dialog-description"
      >
        <DialogTitle id="tax-office-number-dialog-title">Tax office number missing</DialogTitle>
        <DialogContent>
          <DialogContentText id="tax-office-number-dialog-description">
            This customer has no tax office number. Do you still want to continue the download?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTaxOfficeNumberDialog({ isOpen: false })}
            color="primary"
            aria-label="Cancel XML download"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setTaxOfficeNumberDialog({ isOpen: false });
              handleXMLDownload({
                skipVatRequiredWarning: true,
                skipUncheckedDocumentsWarning: true,
                skipMissingPolishNawWarning: true,
                skipCesMissingWarning: true,
                skipTaxOfficeNumberWarning: true,
              });
            }}
            color="primary"
            autoFocus
            aria-label="Continue XML download without tax office number"
          >
            Continue download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={steuerNummerDialog.isOpen}
        onClose={() => setSteuerNummerDialog({ isOpen: false })}
        aria-labelledby="steuer-nummer-dialog-title"
        aria-describedby="steuer-nummer-dialog-description"
      >
        <DialogTitle id="steuer-nummer-dialog-title">Steuernummer missing</DialogTitle>
        <DialogContent>
          <DialogContentText id="steuer-nummer-dialog-description">
            This German customer has no Steuernummer. The XML will be generated without it. Do you still want to
            continue the download?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSteuerNummerDialog({ isOpen: false })}
            color="primary"
            aria-label="Cancel XML download"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSteuerNummerDialog({ isOpen: false });
              handleXMLDownload({
                skipVatRequiredWarning: true,
                skipUncheckedDocumentsWarning: true,
                skipMissingPolishNawWarning: true,
                skipCesMissingWarning: true,
                skipTaxOfficeNumberWarning: true,
                skipSteuerNummerWarning: true,
              });
            }}
            color="primary"
            autoFocus
            aria-label="Continue XML download without Steuernummer"
          >
            Continue download
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
